import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { Popover, Table } from "antd";
import React from "react";
import { AiOutlinePlus } from "react-icons/ai";
import Loader from "../../Components/Loader/Loader";
import Pagination from "../../Components/Pagination/Pagination";
import BuyerDeleteModal from "./BuyerModals/BuyerDeleteModal";
import BuyerAssignIndustryModal from "./BuyerModals/BuyerAssignIndustryModal";
import BuyerOverrideModal from "./BuyerModals/BuyerOverrideModal";
import BuyerBulkUploadModal from "./BuyerModals/BuyerBulkUploadModal";
import BuyerAdvancedSearch from "./BuyerAdvancedSearch";
import BuyerRenewModal from "./BuyerModals/BuyerRenewModal";
import { useBuyerPage, emptyBuyerSearch, PAGESIZE } from "./useBuyerPage";
import { getBuyerTableConfig } from "./buyerTableColumns";

// Claude: refactored — state/handlers → useBuyerPage, columns → buyerTableColumns 20-Apr-2026

const Buyer = () => {
  const {
    loading, buyersList, totalBuyersCount,
    currentPage, setCurrentPage, selectedRowKeys, selectedRows, selectAllDB, excludedIds,
    isBulkModalVisible, handleBulkOk, isBulkConfirmModalVisible, bulkUploadDone,
    bulkDocument, setBulkDocument, fileInputKey, bulkEventId,
    showBulkModal, showBulkConfirmModal, hideBulkConfirmModal,
    eventData, sortEventId, setSortEventId, pendingEventId, setPendingEventId,
    isDeleteModalOpen, handleDeleteModalOk, showDeleteModal, handleDeleteOk, handleUnlinkOk,
    buyerPlansData,
    industryNodes, isIndustryModalVisible, setIsIndustryModalVisible,
    assignIndustryIds, setAssignIndustryIds, handleAssignIndustry,
    appliedSearch, setAppliedSearch,
    pendingSearch, setPendingSearch, pendingSearchModes, setPendingSearchModes,
    isOverrideModalOpen, setIsOverrideModalOpen, overrideBuyerId,
    overrideStatus, setOverrideStatus, overrideReason, setOverrideReason,
    overrideNotes, domainConflictEmail, setDomainConflictEmail, overrideCurrentStatus,
    visibleColKeys, colPopoverOpen, setColPopoverOpen, toggleCol,
    handleBulkUpload, handleOverrideVerification,
    resetPasswordClick, handleSelectAllFromDB, getBuyersData,
    handleEditClick, handleRewardsClick, handleRenewClick, handleOverrideClick,
    handleAddClick, clearSelection, handleExportCsv,
    isRenewModalOpen, setIsRenewModalOpen, renewTargetBuyer, renewLoading, handleConfirmRenew,
    rowSelection, generateRowKey, error,
  } = useBuyerPage();

  const { columns, colChooserContent } = getBuyerTableConfig({
    buyerPlansData, industryNodes, visibleColKeys, toggleCol, colPopoverOpen, setColPopoverOpen,
    onOverrideClick: handleOverrideClick,
    onEditClick: handleEditClick,
    onRewardsClick: handleRewardsClick,
    onRenewClick: handleRenewClick,
  });

  return (
    <div className="p-4">
      {loading ? <Loader /> : null}

      <BuyerDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalOk}
        sortEventId={sortEventId}
        selectAllDB={selectAllDB}
        totalBuyersCount={totalBuyersCount}
        excludedIds={excludedIds}
        selectedRowKeys={selectedRowKeys}
        onConfirmDelete={() => { handleDeleteOk(selectedRows); handleDeleteModalOk(); }}
        onConfirmUnlink={() => { handleUnlinkOk(selectedRows); handleDeleteModalOk(); }}
      />

      <BuyerAssignIndustryModal
        isOpen={isIndustryModalVisible}
        onClose={() => setIsIndustryModalVisible(false)}
        industryNodes={industryNodes}
        assignIndustryIds={assignIndustryIds}
        setAssignIndustryIds={setAssignIndustryIds}
        onAssign={handleAssignIndustry}
      />

      <BuyerOverrideModal
        isOpen={isOverrideModalOpen}
        onClose={() => setIsOverrideModalOpen(false)}
        overrideNotes={overrideNotes}
        overrideBuyerId={overrideBuyerId}
        overrideStatus={overrideStatus}
        setOverrideStatus={setOverrideStatus}
        overrideReason={overrideReason}
        setOverrideReason={setOverrideReason}
        domainConflictEmail={domainConflictEmail}
        setDomainConflictEmail={setDomainConflictEmail}
        overrideCurrentStatus={overrideCurrentStatus}
        onConfirm={handleOverrideVerification}
      />

      <BuyerBulkUploadModal
        isOpen={isBulkModalVisible}
        onClose={handleBulkOk}
        bulkDocument={bulkDocument}
        setBulkDocument={setBulkDocument}
        fileInputKey={fileInputKey}
        onShowConfirm={showBulkConfirmModal}
        isConfirmOpen={isBulkConfirmModalVisible}
        onConfirmClose={hideBulkConfirmModal}
        onConfirmSubmit={handleBulkUpload}
        bulkUploadDone={bulkUploadDone}
      />

      <BuyerRenewModal
        isOpen={isRenewModalOpen}
        onClose={() => setIsRenewModalOpen(false)}
        buyer={renewTargetBuyer}
        plansData={buyerPlansData}
        onConfirm={handleConfirmRenew}
        loading={renewLoading}
      />

      {/* Claude: toolbar — advanced search always visible; buyerCompanyName moved inside panel 20-05-2026 */}
      <Box mt={1} mb={1}>
        <Text fontSize="md" fontWeight={600} mb={1}>Buyers</Text>
        <Flex gap={2} alignItems="flex-end" flexWrap="wrap">
          {(sortEventId || Object.entries(appliedSearch).some(([_, v]) => v)) && (
            <Text fontSize="xs" color="blue.500" mb="4px">Filters active</Text>
          )}
          <Box flex={1} />
          <Button size="sm" fontSize="xs" h="28px" background="#0E122F" color="white" px={3} gap={2} _hover={{ background: "#2E7D32" }}
            onClick={handleAddClick}
          >
            <AiOutlinePlus /> Buyer
          </Button>
          <Button size="sm" fontSize="xs" h="28px" background="#0E122F" color="white" px={3} _hover={{ background: "#2E7D32" }} onClick={showBulkModal}>
            Bulk Upload
          </Button>
          <Button size="sm" fontSize="xs" h="28px" background="#0E122F" color="white" px={3} _hover={{ background: "#2E7D32" }}
            onClick={() => handleExportCsv(sortEventId, appliedSearch)}
          >
            Export CSV
          </Button>
        </Flex>

        <BuyerAdvancedSearch
          pendingSearch={pendingSearch}
          setPendingSearch={setPendingSearch}
          pendingSearchModes={pendingSearchModes}
          setPendingSearchModes={setPendingSearchModes}
          pendingEventId={pendingEventId}
          setPendingEventId={setPendingEventId}
          emptySearch={emptyBuyerSearch}
          eventData={eventData}
          industryNodes={industryNodes}
          buyerPlansData={buyerPlansData}
          onReset={() => { setPendingSearch({ ...emptyBuyerSearch }); setAppliedSearch({ ...emptyBuyerSearch }); setPendingSearchModes({}); setPendingEventId(""); setSortEventId(""); setCurrentPage(1); }}
          onApply={() => { setAppliedSearch({ ...pendingSearch }); setSortEventId(pendingEventId); setCurrentPage(1); }}
        />
      </Box>

      <Box mt={1}>
        <Flex justifyContent="start" mb={1} gap={1} alignItems="center">
          <Button size="xs" fontSize="xs" background="#0E122F" color="white" px={3} _hover={{ background: "#2E7D32" }} onClick={() => resetPasswordClick(selectedRows)}>Reset Password</Button>
          <Button size="xs" fontSize="xs" background="#0E122F" color="white" px={3} _hover={{ background: "#2E7D32" }}
            onClick={() => { if (selectedRows.length === 0) { error("Please select at least one buyer"); return; } setAssignIndustryIds([]); setIsIndustryModalVisible(true); }}
          >
            Assign Industry
          </Button>
          <Button size="xs" fontSize="xs" background="secondary.light" color="secondary.main" px={3} _hover={{ background: "#C62828", color: "white" }}
            onClick={() => { if (selectedRows.length > 0) showDeleteModal(); else error("Please select atleast one entry to delete !"); }}
          >
            Delete
          </Button>
          <Text fontSize="xs" color="gray.500" ml={2}>{totalBuyersCount === 0 ? "No buyers" : `${totalBuyersCount} buyers`}</Text>
          <Popover content={colChooserContent} trigger="click" open={colPopoverOpen} onOpenChange={setColPopoverOpen} placement="bottomRight">
            <Button size="xs" fontSize="xs" background="#0E122F" color="white" px={3} ml="auto" _hover={{ background: "#2E7D32" }}>Columns</Button>
          </Popover>
        </Flex>

        {((selectedRowKeys.length === buyersList.length && buyersList.length > 0) || selectAllDB) && (
          <Flex mb={1} px={2} py={1} bg="blue.50" borderRadius="md" alignItems="center" gap={2} fontSize="xs">
            {selectAllDB ? (
              <>
                <Text>All <strong>{totalBuyersCount - excludedIds.length}</strong> buyers are selected.</Text>
                <Text color="blue.600" cursor="pointer" textDecoration="underline" onClick={clearSelection}>Clear selection</Text>
              </>
            ) : (
              <>
                <Text><strong>{buyersList.length}</strong> buyers on this page are selected.</Text>
                {totalBuyersCount > buyersList.length && (
                  <Text color="blue.600" cursor="pointer" textDecoration="underline" onClick={handleSelectAllFromDB}>
                    Select all {totalBuyersCount} buyers from the database
                  </Text>
                )}
              </>
            )}
          </Flex>
        )}

        <Box sx={{ "& .ant-table": { fontSize: "12px" }, "& .ant-table-cell": { fontSize: "12px" } }}>
          <Table size="small" style={{ width: "100%" }} tableLayout="fixed" rowSelection={{ type: "checkbox", ...rowSelection }}
            dataSource={buyersList} bordered columns={columns} pagination={false} rowKey={generateRowKey}
          />
        </Box>
        <Flex justifyContent="end">
          <Pagination current={currentPage} total={totalBuyersCount} onChange={(n) => setCurrentPage(n)} pageSize={PAGESIZE} />
        </Flex>
      </Box>
    </div>
  );
};

export default Buyer;
